<template>
  <div class="w-100">
    <div class="payout-bulk-generation">
      <div class="row">
        <div class="col col-12 col-md-2">
          <label class="form__label">Copy Period From:</label>
          <Datepicker
            :bootstrap-styling="true"
            :value="_toIsoStringTime(periodMonthFrom)"
            @input="periodMonthFrom = Moment($event).format(defaultDateFormat)"
            format="yyyy-MM"
            minimum-view="month"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">Copy Period To:</label>
          <Datepicker
            :bootstrap-styling="true"
            :value="_toIsoStringTime(periodMonthTo)"
            @input="periodMonthTo = Moment($event).format(defaultDateFormat)"
            format="yyyy-MM"
            minimum-view="month"
          />
        </div>
        <div class="col col-12 col-md-2">
          <label class="form__label">Create For:</label>
          <Datepicker
            :bootstrap-styling="true"
            :value="_toIsoStringTime(createForMonth)"
            @input="createForMonth = Moment($event).format(defaultDateFormat)"
            format="yyyy-MM"
            minimum-view="month"
          />
        </div>
        <div class="col col-12 col-md-3 align-self-end">
          <b-button
            @click.prevent="showCreationModal = true"
            class="button ml-2 float-left"
            type="submit"
            variant="primary">
            Generate expenses
          </b-button>
        </div>
      </div>
    </div>
    <b-modal
      @hide="hideCreationModal"
      class="modal"
      hide-footer
      size="lg"
      v-model="showCreationModal"
    >
      <template v-if="isDataLoading === false">
        <div class="row ml-2">
          <div class="col">
            <div class="float-left">Default values for new expenses: due date - {{ Moment(copyDueDate).format('DD MMM') }}, paid date - {{ Moment(copyPaidDate).format('DD MMM') }}</div>
            <div class="float-right mb-2" style="font-size: 1.4em;">Expenses for {{ Moment(createForMonth).format('MMM YYYY') }} (<span v-html="getItemsCountLabel(getCountTotal().copy)"/>)</div>
            <hr style="margin-top: 30px;" />
          </div>
        </div>
        <template v-if="expensesData.length">
          <div
            :key="monthIndex"
            class="row mb-2 ml-2"
            v-for="({ originMonth, categories }, monthIndex) in expensesData"
          >
            <div class="col">
              <div>Expenses for {{ Moment(originMonth).format('MMM YYYY') }} (<span v-html="getItemsCountLabel(getCountInMonth(expensesData[monthIndex]).origin)"/>)</div>
              <b-card :key="monthIndex + ' ' + categoryIndex" class="mb-1" no-body v-for="({ name, items }, categoryIndex) in categories">
                <b-card-header class="p-1" header-tag="header" role="tab">
                  <b-button block v-b-toggle="'expense-category-' + monthIndex + '-' + categoryIndex" variant="light">
                    {{ name }} (<span v-html="getItemsCountLabel(getCountInCategory(categories[categoryIndex]).origin) + ' ⇨ ' + getItemsCountLabel(getCountInCategory(categories[categoryIndex]).copy)"/>)
                  </b-button>
                </b-card-header>
                <b-collapse :id="'expense-category-' + monthIndex + '-' + categoryIndex" role="tabpanel">
                  <b-card-body>
                    <div
                      :key="monthIndex + ' ' + categoryIndex + ' ' + expenseIndex"
                      class="row mb-2"
                      v-for="({ origin, copy, status }, expenseIndex) in items"
                    >
                      <div class="col-5">
                        <template v-if="origin">
                          {{ origin.due_date }}
                          <span v-if="origin.paid_date"> / {{origin.paid_date}}</span>
                          <div class="float-right">
                            <ExpensesLinks
                              :item="origin"
                              @deletedExpense="deleteExpense(monthIndex, categoryIndex, expenseIndex, 'origin')"
                              @savedExpense="savedExpenseManually(expensesData[monthIndex]['categories'][categoryIndex]['items'][expenseIndex], 'origin', $event)"
                            />
                          </div>
                          <div :title="origin.comment" class="text-overflow-handle">{{origin.amount}} {{origin.currency.code}} | {{origin.comment}}</div>
                        </template>
                        <template v-else>
                          No related expense
                        </template>
                      </div>
                      <div class="col-2 message-status text-center">
                        <div @click.prevent="copyExpense(expensesData[monthIndex]['categories'][categoryIndex]['items'][expenseIndex])"
                             class="button btn btn-success"
                             v-if="status === 'no_copy'">
                          <font-awesome-icon icon="copy" style="margin:2px 7px 0px 0px"/> Copy &#8680;</div>
                        <div class="spinner-border text-primary" v-else-if="status === 'creating'">
                          <span class="sr-only">Creating...</span>
                        </div>
                        <div v-else-if="status === 'created'">
                          <div class="status-success status-icon" title="Created" v-b-tooltip.html>&check;</div>
                        </div>
                        <div v-else-if="status === 'error'">
                          <div class="status-success status-icon" title="Error" v-b-tooltip.html>&#9888;</div>
                        </div>
                      </div>
                      <div class="col-5">
                        <template v-if="copy">
                          {{copy.due_date}}
                          <span v-if="copy.paid_date"> / {{copy.paid_date}}</span>
                          <div class="float-right">
                            <ExpensesLinks
                              :item="copy"
                              @deletedExpense="deleteExpense(monthIndex, categoryIndex, expenseIndex, 'copy')"
                              @savedExpense="savedExpenseManually(expensesData[monthIndex]['categories'][categoryIndex]['items'][expenseIndex], 'copy', $event)"
                            />
                          </div>
                          <div :title="copy.comment" class="text-overflow-handle">{{copy.amount}} {{copy.currency.code}} | {{copy.comment}}</div>
                        </template>
                        <template v-else>
                          No expense
                        </template>
                      </div>
                    </div>
                  </b-card-body>
                </b-collapse>
              </b-card>
            </div>
          </div>
        </template>
        <template v-else>
          No Expenses
        </template>
      </template>
      <template v-else>
        <div class="loading-message">
          <h5>Loading, please wait</h5>
        </div>
        <Preloader />
      </template>
    </b-modal>

    <FinancesExpensesForm
      :bankCards="bankCards"
      :currencies="currencies"
      :expensesCategories="expensesCategories"
      :expensesStatuses="expensesStatuses"
      :isLoading="isDataLoading"
      :ownBankDetails="ownBankDetails"
      :projects="getProjects"
      :users="getAllUsers"
      :value="false"
      ref="expenseModalAuto"
    />
  </div>
</template>

<script>
  import {cloneDeep} from 'lodash'
  import Moment from 'moment'
  import Datepicker from 'vuejs-datepicker'
  import {mapActions, mapGetters} from 'vuex'
  import {permissions} from '@mocks/permissions'
  import Preloader from '@components/Preloader'
  import FinancesExpensesForm from '../components/forms/FinancesExpensesForm'
  import ExpensesLinks from '../components/ExpensesLinks'
  import ExpenseForm from '../mixins/ExpenseForm'

  export default {
  name: 'ExpensesBulkCreation',
  mixins: [ExpenseForm],
  components: {
    Datepicker,
    Preloader,
    FinancesExpensesForm,
    ExpensesLinks
  },
  data () {
    const defaultDateFormat = 'YYYY-MM'
    return {
      permissions,
      defaultDateFormat,
      showCreationModal: false,
      isDataPageLoading: false,
      expensesTouched: false,
      periodMonthFrom: Moment().subtract(3, 'months').startOf('month').format(defaultDateFormat),
      periodMonthTo: Moment().subtract(2, 'months').endOf('month').format(defaultDateFormat),
      createForMonth: Moment().subtract(1, 'months').endOf('month').format(defaultDateFormat),
      isDataLoading: false,
      expensesData: []
    }
  },
  mounted () {
    this.expensesTouched = false
  },
  computed: {
    ...mapGetters({
      getIsModalLoading: 'finances/getIsModalLoading'
    }),
    getActualProjects () {
      return this.getProjects
        .filter(project => project.project_status.name !== 'done')
    },
    copyDueDate () {
      return this.Moment(this.createForMonth).date(17)
    },
    copyPaidDate () {
      return this.Moment(this.createForMonth).date(15)
    }
  },
  watch: {
    'showCreationModal': function (val) {
      if (val) {
        this.fetchDataForBulkCreation()
      }
    }
  },
  methods: {
    Moment: function (date) {
      return Moment(date)
    },
    ...mapActions({
      setIsModalLoading: 'finances/setIsModalLoading'
    }),
    async fetchDataForBulkCreation () {
      this.showCreationModal = true

      this.expensesData = []
      this.isDataLoading = true
      this.expensesData = await this.fetchExpensesData()
      this.isDataLoading = false
    },
    async hideCreationModal (event) {
      if (event.trigger !== 'confirmed') {
        event.preventDefault()
      }
      const confirm = await this.$root.$confirm.open(
        'Close Bulk Creation',
        'Are you sure you want to stop bulk creation process and close this window?'
      )
      if (confirm) {
        event.vueTarget.hide('confirmed')
        this.showCreationModal = false
        if (this.expensesTouched) {
          this.$emit('reloadExpenses')
        }
      }
    },
    saveMessageSuccess (expenseItem, item) {
      const itemAttrName = expenseItem.itemAttrName ?? 'copy'
      if (itemAttrName === 'copy') {
        expenseItem.status = 'created'
      }
      expenseItem[itemAttrName] = item ? Object.assign(expenseItem[itemAttrName], item) : expenseItem[itemAttrName]
      expenseItem.errors = []
    },
    saveMessageError (expenseItem, errors) {
      expenseItem.status = 'error'
      expenseItem.errors = expenseItem.errors ? expenseItem.errors.push(errors) : errors
    },
    savedExpenseManually (expenseItem, attrName, { status, data }) {
      expenseItem.itemAttrName = attrName
      if (status === 200) {
        this.saveMessageSuccess(expenseItem, data)
      } else {
        const errors = ['Error expense saving']
        this.saveMessageError(expenseItem, errors)
      }
      this.expensesTouched = true
    },
    deleteExpense (monthIndex, categoryIndex, expenseIndex, attrName) {
      const expenseItem = this.expensesData[monthIndex]['categories'][categoryIndex]['items'][expenseIndex]
      expenseItem[attrName] = null
      if (attrName === 'copy') {
        expenseItem['status'] = 'no_copy'
      }
      if (!expenseItem['origin'] && !expenseItem['copy']) {
        this.expensesData[monthIndex]['categories'][categoryIndex]['items'].splice(expenseIndex, 1)
        if (this.expensesData[monthIndex]['categories'][categoryIndex]['items'].length === 0) {
          this.expensesData[monthIndex]['categories'].splice(categoryIndex, 1)
        }
      }
      this.expensesTouched = true
    },
    async fetchExpensesData () {
      const response = await this.axios
        .post('/api/finances/expenses/data-for-bulk-creation',
          {
            period_month_from: this.periodMonthFrom,
            period_month_to: this.periodMonthTo,
            create_for_month: this.createForMonth
          },
          { headers: { Authorization: 'Bearer' + localStorage.getItem('access_token') } })
        .catch((error) => {
          this.errorNotifications(error)
        })
      return response.data
    },
    async copyExpense (item) {
      const copy = cloneDeep(item.origin)
      copy.expenses_status = this.getExpenseStatus('paid')
      copy.due_date = this.copyDueDate
      copy.paid_date = this.copyPaidDate
      item.status = 'creating'

      const createParams = this.prepareDataForEditAndCreateRequest(copy)
      createParams.origin_id = item.origin.id
      const expenseResponse = await this._toggleDataPageLoading(async () => {
        return this.$api.expenses.createExpenses(createParams)
      })
      if (expenseResponse.data) {
        item.copy = expenseResponse.data
        item.status = 'created'
      } else {
        item.copy = copy
        item.status = 'error'
      }
      this.expensesTouched = true
    },
    getExpenseStatus (name) {
      return this.$refs['expenseModalAuto'].expensesStatuses.find(el => el.name === name)
    },
    getItemsCountLabel (itemCount) {
      if (itemCount === 0) {
        return 'no items'
      } else {
        return '<b>' + itemCount + '</b>' + ' item' + (itemCount > 1 ? 's' : '')
      }
    },
    getCountInCategory (category) {
      return {
        origin: category.items.filter(expense => expense.origin).length,
        copy: category.items.filter(expense => expense.copy).length
      }
    },
    getCountInMonth (month) {
      let origin = 0
      let copy = 0
      month.categories.forEach(category => {
        origin += this.getCountInCategory(category).origin
        copy += this.getCountInCategory(category).copy
      })
      return { origin, copy }
    },
    getCountTotal () {
      let origin = 0
      let copy = 0
      this.expensesData.forEach(month => {
        origin += this.getCountInMonth(month).origin
        copy += this.getCountInMonth(month).copy
      })
      return { origin, copy }
    }
  }
}
</script>

<style lang="scss">
  .status-icon {
    font-size: x-large;
  }
  .status-error {
    color: red;
  }
  .status-success {
    color: #3fa52f;
  }
  .status-warning {
    color: red;
  }
  .small-spinner.spinner-border {
    width: 1rem;
    height: 1rem;
  }
  .message-item {
    display: flex;
  }
  .message-item-right-box {
    margin-left: auto;
  }
  .loading-message {
    text-align:center;
  }
  .ep-message {
    margin-left: 10px;
  }
  .text-overflow-handle {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis
  }
</style>
